import * as React from 'react';
import ReactDatePicker from 'react-datepicker';
import uniqueId from 'lodash/uniqueId';
import moment, {type Moment} from 'moment';

import 'react-datepicker/dist/react-datepicker.css';
import './date-picker-override.css';
import css from './date-picker.css';

import {classify} from 'src/utils/index';

import CalendarIcon from 'src/images/calendar-icon.svg';


export type DatePickerProps = {
  className?: string;
  defaultValue?: Moment | null;
  onChange?: (date?: Moment | null) => void;
  selected?: Moment | null;
  disabled?: boolean;
  minDate?: Moment;
  maxDate?: Moment;
  /** Legacy call-site prop; not forwarded to react-datepicker. */
  type?: string;
  /** Legacy call-site prop; styling handled via className. */
  error?: string;
  name?: string;
  placeholderText?: string;
  preventOpenOnFocus?: boolean;
  isClearable?: boolean;
};

export default function DatePicker({
  className,
  defaultValue = null,
  selected,
  onChange,
  minDate,
  maxDate,
  disabled,
  type: _type,
  error: _error,
  name,
  placeholderText,
  preventOpenOnFocus,
  isClearable,
}: DatePickerProps): React.ReactElement | null {
  const [mounted, setMounted] = React.useState(false);
  const [value, setValue] = React.useState<Moment | null>(defaultValue);
  const [elementId] = React.useState(() => uniqueId('date-picker'));
  const pickerRef = React.useRef<ReactDatePicker | null>(null);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  const handleChange = onChange ?? ((date) => setValue(date ?? null));

  const handleClickOutside = () => {
    pickerRef.current?.cancelFocusInput();
  };

  const resolvedSelected = selected ?? value;
  const selectedDate = resolvedSelected?.isValid()
    ? resolvedSelected.toDate()
    : null;

  return (
    <div
      className={classify(css.datePicker, className, {
        [css.disabled]: disabled,
      })}
    >
      <ReactDatePicker
        id={elementId}
        ref={pickerRef}
        selected={selectedDate}
        dateFormat="yyyy-MM-dd"
        onChange={(date: Date | null) =>
          handleChange(date ? moment(date) : null)
        }
        onClickOutside={handleClickOutside}
        minDate={minDate?.isValid() ? minDate.toDate() : undefined}
        maxDate={maxDate?.isValid() ? maxDate.toDate() : undefined}
        disabled={disabled}
        name={name}
        placeholderText={placeholderText}
        preventOpenOnFocus={preventOpenOnFocus}
        isClearable={isClearable}
      />
      <label htmlFor={elementId} className={css.label}>
        <CalendarIcon />
      </label>
    </div>
  );
}
